import { OctagonAlert, ShieldAlert, ShieldCheck } from 'lucide-react';
import type { DisciplineCycleStatus } from 'shared';

import { useDisciplineCycle, useResetDisciplineCycle } from '../../features/discipline/queries';
import { formatCurrency } from '../../lib/format/currency';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';

interface LatestSnapshotRef {
  id: string;
  nav: string;
  asOfDate: string;
}

interface PortfolioDisciplineCardProps {
  accountId: string | null;
  currency: string;
  latestSnapshot: LatestSnapshotRef | null;
}

function statusCopy(status: DisciplineCycleStatus): {
  title: string;
  action: string;
  tone: 'success' | 'warning' | 'danger';
} {
  if (status === 'DE_RISK') {
    return {
      title: 'DE-RISK',
      action: 'DE-RISK REQUIRED — no new buys or adds. Reduce gross exposure toward 60% NAV.',
      tone: 'danger',
    };
  }
  if (status === 'PROTECT_PROFITS') {
    return {
      title: 'PROTECT PROFITS',
      action: 'DO NOT INCREASE EXPOSURE — protect the cycle and review weakest duplicate risk.',
      tone: 'warning',
    };
  }
  return {
    title: 'NORMAL',
    action: 'NO ACTION REQUIRED — follow the plan.',
    tone: 'success',
  };
}

function StatusIcon({ status }: { status: DisciplineCycleStatus }) {
  if (status === 'DE_RISK') return <OctagonAlert className="h-7 w-7" aria-hidden="true" />;
  if (status === 'PROTECT_PROFITS') {
    return <ShieldAlert className="h-7 w-7" aria-hidden="true" />;
  }
  return <ShieldCheck className="h-7 w-7" aria-hidden="true" />;
}

function pct(value: string): string {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '-';
  return `${(numeric * 100).toFixed(1)}%`;
}

export function PortfolioDisciplineCard({
  accountId,
  currency,
  latestSnapshot,
}: PortfolioDisciplineCardProps) {
  const cycle = useDisciplineCycle(accountId);
  const reset = useResetDisciplineCycle();

  if (!accountId) {
    return (
      <Card elevation="primary">
        <p className="text-sm text-text-muted">
          Create or select a swing account to enable profit protection.
        </p>
      </Card>
    );
  }

  if (cycle.isLoading) {
    return (
      <Card elevation="primary" aria-label="Portfolio discipline loading">
        <div className="h-28 animate-pulse rounded bg-bg-muted" aria-hidden="true" />
      </Card>
    );
  }

  if (cycle.isError) {
    return (
      <Card elevation="primary">
        <Badge tone="danger">Discipline status unavailable</Badge>
        <p className="mt-3 text-sm text-text-muted">
          The portfolio protection state could not be loaded. Do not infer a NORMAL state from this.
        </p>
      </Card>
    );
  }

  if (!cycle.data) {
    return (
      <Card
        elevation="primary"
        header={
          <div>
            <Badge tone="warning" size="md">
              DISCIPLINE CYCLE NOT STARTED
            </Badge>
            <p className="mt-2 text-sm text-text-muted">
              Start from an explicit recorded NAV snapshot to enable high-water profit protection.
            </p>
          </div>
        }
      >
        {latestSnapshot ? (
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="text-sm text-text-muted">Latest recorded NAV</div>
              <div className="mt-1 text-xl font-semibold tabular-nums">
                {formatCurrency(latestSnapshot.nav, currency)}
              </div>
              <div className="text-sm text-text-muted">{latestSnapshot.asOfDate}</div>
            </div>
            <Button
              className="bg-blue-600 hover:bg-blue-700"
              onClick={() => reset.mutate({ accountId, snapshotId: latestSnapshot.id })}
              disabled={reset.isPending}
              aria-label="Start discipline cycle from latest recorded NAV"
            >
              {reset.isPending ? 'Starting…' : 'Start from this NAV'}
            </Button>
          </div>
        ) : (
          <p className="text-sm text-text-muted">
            Record a NAV snapshot first, then start the discipline cycle from that snapshot.
          </p>
        )}
        {reset.isError ? (
          <p className="mt-3 text-sm text-danger">Could not start the discipline cycle.</p>
        ) : null}
      </Card>
    );
  }

  const copy = statusCopy(cycle.data.status);
  const hasExternalCashFlow = Number(cycle.data.netExternalCashFlow) !== 0;

  return (
    <Card
      elevation="primary"
      header={
        <div className="flex items-center gap-3">
          <StatusIcon status={cycle.data.status} />
          <div>
            <Badge tone={copy.tone} size="lg">
              {copy.title}
            </Badge>
            <p className="mt-2 text-sm font-medium">{copy.action}</p>
          </div>
        </div>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <div>
          <div className="text-sm text-text-muted">Current NAV</div>
          <div className="mt-1 text-xl font-semibold tabular-nums">
            {formatCurrency(cycle.data.currentNav, currency)}
          </div>
          {hasExternalCashFlow ? (
            <div className="mt-1 text-xs text-text-muted">
              Cash-flow adjusted: {formatCurrency(cycle.data.cashFlowAdjustedNav, currency)}
            </div>
          ) : null}
        </div>
        <div>
          <div className="text-sm text-text-muted">Reset NAV</div>
          <div className="mt-1 text-xl font-semibold tabular-nums">
            {formatCurrency(cycle.data.resetNav, currency)}
          </div>
        </div>
        <div>
          <div className="text-sm text-text-muted">Cash-flow-adjusted HWM</div>
          <div className="mt-1 text-xl font-semibold tabular-nums">
            {formatCurrency(cycle.data.highWaterNav, currency)}
          </div>
        </div>
        <div>
          <div className="text-sm text-text-muted">Peak profit</div>
          <div className="mt-1 text-lg font-semibold tabular-nums">
            {formatCurrency(cycle.data.peakProfit, currency)}
          </div>
        </div>
        <div>
          <div className="text-sm text-text-muted">Given back from HWM</div>
          <div className="mt-1 text-lg font-semibold tabular-nums">
            {formatCurrency(cycle.data.currentGiveback, currency)}
          </div>
        </div>
        <div>
          <div className="text-sm text-text-muted">Giveback of peak profit</div>
          <div className="mt-1 text-lg font-semibold tabular-nums">
            {pct(cycle.data.givebackPctOfPeak)}
          </div>
        </div>
      </div>
      <div className="mt-5 grid gap-2 border-t border-border pt-4 text-sm text-text-muted sm:grid-cols-3">
        <div>Protect threshold: {formatCurrency(cycle.data.protectThresholdNav, currency)}</div>
        <div>De-risk threshold: {formatCurrency(cycle.data.deriskThresholdNav, currency)}</div>
        <div>Cycle reset: {cycle.data.resetDate}</div>
      </div>
      {hasExternalCashFlow ? (
        <p className="mt-3 text-xs text-text-muted">
          External deposits and withdrawals since reset:{' '}
          {formatCurrency(cycle.data.netExternalCashFlow, currency)}. Profit protection uses the
          cash-flow-adjusted NAV so transfers do not count as gains or losses.
        </p>
      ) : null}
    </Card>
  );
}
